/**
 * Quiz Re-Authentication Modal Component
 * Shows when token expires during an untimed quiz
 * Allows student to re-authenticate without losing their progress
 * 
 * CRITICAL: This modal prevents auto-submit for untimed quizzes when token expires
 * Instead, it saves progress locally and prompts for re-authentication
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import logger from '../utils/logger';

export default function QuizReAuthModal({ 
  open, 
  onSuccess, 
  onCancel,
  attemptId,
  quizTitle,
  savedAnswersCount = 0
}) {
  const { login } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  
  const dialogRef = useRef(null);
  const emailInputRef = useRef(null);

  // Pre-fill email from localStorage if available
  useEffect(() => {
    if (open) {
      const storedUser = localStorage.getItem('faithinsight_user');
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          if (user.email) {
            setEmail(user.email);
          }
        } catch (e) {
          // Ignore parse errors
        }
      }
    }
  }, [open]);

  // Scroll lock when open
  useEffect(() => {
    if (!open) return;
    
    const { body } = document;
    const prevOverflow = body.style.overflow;
    const prevTouch = body.style.touchAction;
    const prevScroll = body.style.overscrollBehavior;
    
    body.style.overflow = 'hidden';
    body.style.touchAction = 'none';
    body.style.overscrollBehavior = 'contain';
    
    return () => { 
      body.style.overflow = prevOverflow; 
      body.style.touchAction = prevTouch; 
      body.style.overscrollBehavior = prevScroll; 
    };
  }, [open]);

  // Focus trap + initial focus
  useEffect(() => {
    if (open) {
      const to = setTimeout(() => {
        // Focus password field if email is pre-filled, otherwise focus email
        if (email) {
          document.getElementById('reauth-password')?.focus();
        } else {
          emailInputRef.current?.focus();
        }
      }, 100);
      return () => clearTimeout(to);
    }
  }, [open, email]);

  // Handle escape key - don't allow dismissing for security
  const onKeyDown = useCallback((e) => {
    if (e.key === 'Escape' && open) {
      e.preventDefault();
    }
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      logger.info('Attempting re-authentication during quiz', { attemptId, email });
      
      const result = await login(email, password);
      
      if (result.success) {
        logger.info('Re-authentication successful', { attemptId });
        setPassword(''); // Clear password for security
        onSuccess?.();
      } else {
        setError(result.message || 'Login failed. Please check your credentials.');
        logger.warn('Re-authentication failed', { attemptId, error: result.message });
      }
    } catch (err) {
      logger.error('Re-authentication error', { attemptId, error: err });
      setError(err.message || 'An error occurred. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  if (!open) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="reauth-title"
      aria-describedby="reauth-desc"
      className="reauth-modal-container"
      onKeyDown={onKeyDown}
      style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 10001, // Higher than auto-submit overlay
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '1rem'
      }}
    >
      <div 
        className="reauth-modal-backdrop" 
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'var(--background-overlay)',
          backdropFilter: 'blur(4px)'
        }}
      />
      <div 
        className="reauth-modal-positioner"
        style={{
          position: 'relative',
          width: '100%',
          maxWidth: '420px',
          zIndex: 1
        }}
      >
        <div
          ref={dialogRef}
          className="reauth-modal-content"
          style={{
            background: 'var(--background-primary)',
            borderRadius: 0,
            boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
            overflow: 'hidden',
            maxHeight: '90vh',
            display: 'flex',
            flexDirection: 'column'
          }}
        >
          {/* Header */}
          <div className="reauth-modal-header" style={{ 
            padding: '1.5rem', 
            borderBottom: '1px solid var(--border-color)',
            background: 'linear-gradient(135deg, rgba(252, 129, 129, 0.1) 0%, rgba(246, 224, 94, 0.1) 100%)'
          }}>
            <div className="reauth-modal-icon" style={{ 
              width: '48px', 
              height: '48px', 
              borderRadius: 0, 
              background: 'linear-gradient(135deg, var(--warning-color) 0%, var(--warning-dark) 100%)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: '1rem'
            }}>
              <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                <path d="M7 11V7a5 5 0 0 1 10 0v4" />
              </svg>
            </div>
            <div className="reauth-modal-title-section">
              <h2 id="reauth-title" className="reauth-modal-title" style={{ 
                fontSize: '1.25rem', 
                fontWeight: 600, 
                color: 'var(--text-primary)',
                margin: 0,
                marginBottom: '0.5rem'
              }}>
                Session Expired
              </h2>
              <p className="reauth-modal-subtitle" style={{ 
                fontSize: '0.875rem', 
                color: 'var(--text-muted)',
                margin: 0
              }}>
                Your session has expired. Please log in again to continue your quiz.
              </p>
            </div>
          </div>

          {/* Body */}
          <div className="reauth-modal-body" style={{ padding: '1.5rem' }}>
            {/* Quiz info banner */}
            <div style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.75rem',
              padding: '1rem',
              background: 'rgba(104, 211, 145, 0.1)',
              borderRadius: 0,
              marginBottom: '1.5rem',
              border: '1px solid rgba(104, 211, 145, 0.3)'
            }}>
              <div style={{
                width: '32px',
                height: '32px',
                borderRadius: 0,
                background: 'var(--success-color)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0
              }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2">
                  <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
                  <polyline points="22 4 12 14.01 9 11.01" />
                </svg>
              </div>
              <div>
                <div style={{ 
                  fontSize: '0.75rem', 
                  color: 'var(--success-dark)',
                  fontWeight: 500,
                  marginBottom: '0.25rem'
                }}>
                  Your progress is safe
                </div>
                <div style={{ 
                  fontSize: '0.875rem', 
                  fontWeight: 600, 
                  color: 'var(--success-dark)'
                }}>
                  {savedAnswersCount > 0 
                    ? `${savedAnswersCount} answer${savedAnswersCount !== 1 ? 's' : ''} saved locally`
                    : 'Answers saved locally'}
                </div>
              </div>
            </div>

            <p id="reauth-desc" style={{ 
              fontSize: '0.9375rem', 
              color: 'var(--text-secondary)',
              lineHeight: '1.6',
              margin: 0,
              marginBottom: '1.5rem'
            }}>
              {quizTitle && (
                <span style={{ fontWeight: 500 }}>Quiz: {quizTitle}<br/><br/></span>
              )}
              Your answers have been saved locally. Log in to sync them with the server and continue your quiz.
            </p>

            {/* Login form */}
            <form onSubmit={handleSubmit}>
              {error && (
                <div style={{
                  padding: '0.75rem 1rem',
                  background: 'rgba(252, 129, 129, 0.1)',
                  border: '1px solid var(--error-color)',
                  borderRadius: 0,
                  marginBottom: '1rem',
                  color: 'var(--error-color)',
                  fontSize: '0.875rem'
                }}>
                  {error}
                </div>
              )}

              <div style={{ marginBottom: '1rem' }}>
                <label 
                  htmlFor="reauth-email" 
                  style={{ 
                    display: 'block', 
                    fontSize: '0.875rem', 
                    fontWeight: 500, 
                    color: 'var(--text-primary)',
                    marginBottom: '0.5rem'
                  }}
                >
                  Email
                </label>
                <input
                  ref={emailInputRef}
                  id="reauth-email"
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  required
                  autoComplete="email"
                  style={{
                    width: '100%',
                    padding: '0.75rem 1rem',
                    border: '1px solid var(--border-color)',
                    borderRadius: 0,
                    fontSize: '1rem',
                    background: 'var(--background-primary)',
                    color: 'var(--text-primary)',
                    boxSizing: 'border-box'
                  }}
                />
              </div>

              <div style={{ marginBottom: '1.5rem' }}>
                <label 
                  htmlFor="reauth-password" 
                  style={{ 
                    display: 'block', 
                    fontSize: '0.875rem', 
                    fontWeight: 500, 
                    color: 'var(--text-primary)',
                    marginBottom: '0.5rem'
                  }}
                >
                  Password
                </label>
                <div style={{ position: 'relative' }}>
                  <input
                    id="reauth-password"
                    type={showPassword ? 'text' : 'password'}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    autoComplete="current-password"
                    style={{
                      width: '100%',
                      padding: '0.75rem 3rem 0.75rem 1rem',
                      border: '1px solid var(--border-color)',
                      borderRadius: 0,
                      fontSize: '1rem',
                      background: 'var(--background-primary)',
                      color: 'var(--text-primary)',
                      boxSizing: 'border-box'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    style={{
                      position: 'absolute',
                      right: '0.75rem',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      padding: '0.25rem',
                      color: 'var(--text-muted)'
                    }}
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                        <line x1="1" y1="1" x2="23" y2="23" />
                      </svg>
                    ) : (
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                        <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                        <circle cx="12" cy="12" r="3" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={isLoading || !email || !password}
                style={{
                  width: '100%',
                  padding: '0.875rem 1.5rem',
                  borderRadius: 0,
                  border: 'none',
                  background: isLoading || !email || !password 
                    ? 'var(--text-muted)' 
                    : 'linear-gradient(135deg, var(--primary-color) 0%, var(--primary-dark) 100%)',
                  color: 'var(--text-inverse)',
                  fontWeight: 600,
                  fontSize: '1rem',
                  cursor: isLoading || !email || !password ? 'not-allowed' : 'pointer',
                  transition: 'all 0.2s',
                  minHeight: '48px',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '0.5rem'
                }}
              >
                {isLoading ? (
                  <>
                    <svg 
                      width="20" 
                      height="20" 
                      viewBox="0 0 24 24" 
                      fill="none" 
                      stroke="currentColor" 
                      strokeWidth="2"
                      style={{ animation: 'spin 1s linear infinite' }}
                    >
                      <path d="M21 12a9 9 0 1 1-6.219-8.56" />
                    </svg>
                    Logging in...
                  </>
                ) : (
                  <>
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4" />
                      <polyline points="10 17 15 12 10 7" />
                      <line x1="15" y1="12" x2="3" y2="12" />
                    </svg>
                    Log In & Continue Quiz
                  </>
                )}
              </button>
            </form>
          </div>

          {/* Footer with cancel option */}
          {onCancel && (
            <div style={{ 
              padding: '1rem 1.5rem', 
              borderTop: '1px solid var(--border-color)',
              background: 'var(--background-secondary)'
            }}>
              <button
                onClick={onCancel}
                style={{
                  width: '100%',
                  padding: '0.75rem 1rem',
                  borderRadius: 0,
                  border: '1px solid var(--border-color)',
                  background: 'var(--background-primary)',
                  color: 'var(--text-secondary)',
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                Save & Exit (Continue Later)
              </button>
              <p style={{
                fontSize: '0.75rem',
                color: 'var(--text-muted)',
                textAlign: 'center',
                margin: '0.75rem 0 0 0'
              }}>
                Your answers are saved locally and will sync when you return.
              </p>
            </div>
          )}
        </div>
      </div>

      <style>{`
        @keyframes spin {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
        
        .reauth-modal-content input:focus {
          outline: none;
          border-color: var(--primary-color);
          box-shadow: 0 0 0 3px var(--focus-ring);
        }
        
        .reauth-modal-content button:hover:not(:disabled) {
          transform: translateY(-1px);
        }
        
        .reauth-modal-content button:active:not(:disabled) {
          transform: translateY(0);
        }
      `}</style>
    </div>
  );
}
